import { i18n } from '../../i18n'
import alertsAr from '../../i18n/locales/ar/alerts.json'
import alertsEn from '../../i18n/locales/en/alerts.json'
import alertsFr from '../../i18n/locales/fr/alerts.json'

/**
 * The alerts catalog, registered by the feature rather than by `i18n/resources.ts`.
 *
 * Same reasoning as `features/fuel/i18n.ts` and `features/driving/i18n.ts`: the
 * central `NAMESPACES` tuple is shared with every module and is being edited by other
 * agents this phase, and a namespace added there twice is a merge conflict in the one
 * file the app cannot boot without. `addResourceBundle` reaches the same store from
 * inside this directory.
 *
 * `deep = true, overwrite = false` so that if `resources.ts` ever declares `alerts`,
 * the central copy wins key by key and this call becomes a no-op.
 *
 * FR/EN parity is asserted by `catalog-parity.test.ts` beside this file, because the
 * central parity test iterates `NAMESPACES` and would not see a namespace absent
 * from it. A key present in one language only still fails CI.
 *
 * Every component in this feature imports this module for its side effect. A panel
 * rendered on its own — in a focused test, or composed into a future screen — that
 * did not pull the registration in would render raw key paths at a customer.
 * Registration is idempotent.
 */
export const ALERTS_NS = 'alerts'

let registered = false

export function registerAlertsCatalog(): void {
  if (registered) return

  // i18next's store does not exist before `init` and reaching into it throws rather
  // than queueing. In practice `initI18n()` has run, but a feature imported by a
  // script or a future SSR pass must not be the thing that crashes.
  if (!i18n.isInitialized) {
    i18n.on('initialized', registerAlertsCatalog)
    return
  }

  registered = true
  i18n.addResourceBundle('fr', ALERTS_NS, alertsFr, true, false)
  i18n.addResourceBundle('en', ALERTS_NS, alertsEn, true, false)
  i18n.addResourceBundle('ar', ALERTS_NS, alertsAr, true, false)
}

registerAlertsCatalog()
